import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Row, Col, Popconfirm, Badge, Space, Progress, Typography } from 'antd';
import {
	ArrowLeftOutlined,
	DownloadOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
	StopOutlined,
	CalendarOutlined,
	ThunderboltOutlined,
	CheckCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import toast from 'react-hot-toast';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { putApi } from 'redux/sagas/putApiSaga';
import { SERVER_IP } from 'assets/Config';
import { downloadAsFileFromResponseData } from 'helpers';
import { DATE_FORMAT } from 'constants/app-constants';
import { getDateFormat } from 'services/Utils';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import Loader from 'components/loader';
import { MOCK_WORKOUT_PLANS } from 'data/workoutMockData';
import { useI18n } from 'hooks/useI18n';
import './plan-details.scss';

const { Text } = Typography;

const STATUS_COLORS = {
	active: '#52c41a',
	completed: '#1677ff',
	paused: '#fa8c16',
	cancelled: '#ff4d4f',
};

const STATUS_LABELS = {
	active: 'Active',
	completed: 'Completed',
	paused: 'Paused',
	cancelled: 'Cancelled',
};

const normalizeStatus = (status) => String(status || '').toLowerCase();

const getWorkoutDisplayName = (item) => item?.workout?.name || item?.workoutName || 'Workout';

const getWorkoutRestSeconds = (item) => item?.restSec ?? item?.restSeconds ?? item?.workout?.defaultRestSeconds ?? 0;

const formatDetailValue = (value, fallback = 'Not provided') => {
	if (value === 0) return '0';
	return value || fallback;
};

const getExerciseDetailItems = (item) => {
	const workout = item?.workout || {};
	const plannedVolume = item?.sets && item?.reps ? `${item.sets} sets x ${item.reps} reps` : 'Not planned';
	const defaultVolume = workout?.defaultSets && workout?.defaultReps ? `${workout.defaultSets} sets x ${workout.defaultReps} reps` : 'Not provided';

	return [
		{ label: 'Machine / equipment', value: formatDetailValue(workout?.equipment) },
		{ label: 'Muscle group', value: formatDetailValue(workout?.muscleGroup) },
		{ label: 'Training level', value: formatDetailValue(workout?.level) },
		{ label: 'Gender', value: formatDetailValue(workout?.gender) },
		{ label: 'Planned volume', value: plannedVolume },
		{ label: 'Default volume', value: defaultVolume },
		{ label: 'Rest time', value: `${getWorkoutRestSeconds(item)}s` },
		{ label: 'Exercise status', value: workout?.isActive ? 'Active' : 'Inactive' },
	];
};

const PlanDetails = () => {
	const { id } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutPlanRedux = useSelector((state) => state.workoutPlanRedux);
	const loginRedux = useSelector((state) => state.loginRedux);

	const [selectedDayNumber, setSelectedDayNumber] = useState(1);
	const [downloading, setDownloading] = useState(false);

	const handleDownloadPdf = async () => {
		try {
			setDownloading(true);
			const response = await axios({
				url: `${SERVER_IP}api/workoutPlan/${id}/download-report`,
				method: 'GET',
				headers: {
					'Content-Type': 'application/pdf',
					Authorization: `Bearer ${loginRedux.token}`,
				},
				responseType: 'blob',
			});
			await downloadAsFileFromResponseData(response, 'workout-plan-report.pdf');
			toast.success(t('workouts.plan.details.download_success'));
		} catch (err) {
			toast.error(t('workouts.plan.details.download_failed'));
		} finally {
			setDownloading(false);
		}
	};

	const fetchPlan = useCallback(() => {
		dispatch(getApi('GET_WORKOUT_PLAN_BY_ID', `${SERVER_IP}api/workoutPlan/${id}`));
	}, [dispatch, id]);

	useEffect(() => {
		fetchPlan();
	}, [fetchPlan]);

	useEffect(() => {
		const actions = ['PAUSE_WORKOUT_PLAN', 'RESUME_WORKOUT_PLAN', 'CANCEL_WORKOUT_PLAN'];
		for (const action of actions) {
			if (globalRedux.apiStatus[action] === 'SUCCESS') {
				dispatch(resetApiStatus(action));
				fetchPlan();
			}
		}
	}, [globalRedux.apiStatus, dispatch, fetchPlan]);

	const plan = workoutPlanRedux?.currentPlan || MOCK_WORKOUT_PLANS.find((p) => p._id === id);

	const loading = globalRedux.apiStatus.GET_WORKOUT_PLAN_BY_ID === 'PENDING';
	if (loading) return <Loader />;
	if (!plan) return <div style={{ padding: 16 }}>Plan not found.</div>;

	const selectedDay = plan.days?.find((day) => day.dayNumber === selectedDayNumber);
	const normalizedStatus = normalizeStatus(plan.status);
	const completionPercent = plan.durationDays ? Math.round(((plan.currentDay || 0) / plan.durationDays) * 100) : 0;
	const totalExercises = plan.days?.reduce((total, day) => total + (day.items?.length || 0), 0) || 0;
	const completedDays = plan.days?.filter((day) => day.dayNumber < (plan.currentDay || 0)).length || 0;
	const restDaysCount = plan.days?.filter((day) => day.isRestDay).length || 0;
	const currentDayLabel = plan.currentDay ? `Day ${plan.currentDay}` : 'Not started';
	const activeDayCount = Math.max((plan.durationDays || 0) - restDaysCount, 0);
	const selectedDayTitle =
		selectedDay?.title &&
		selectedDay.title !== `Day ${selectedDay.dayNumber}` &&
		selectedDay.title !== 'Rest' &&
		selectedDay.title !== 'Rest Day'
			? selectedDay.title
			: null;
	const selectedDayExerciseCount = selectedDay?.items?.length || 0;
	const selectedDayRestCount = selectedDay?.items?.reduce((total, item) => total + (getWorkoutRestSeconds(item) || 0), 0) || 0;

	const getDayStateClassName = (day) => {
		if (day.isRestDay) return 'is-rest';
		if (day.dayNumber === plan.currentDay) return 'is-current';
		if (day.dayNumber < (plan.currentDay || 0)) return 'is-completed';
		return 'is-upcoming';
	};

	return (
		<div className="plan-details-page">
			<div className="plan-details-shell">
				<section className="plan-details-hero">
					<div className="plan-details-hero__main">
						<div className="plan-details-hero__nav">
							<Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
								Back
							</Button>
							<Button type="primary" onClick={() => navigate(`/workout-plan/customer/${plan.customerId}`)}>
								History
							</Button>
						</div>
						<div className="plan-details-hero__title-wrap">
							<div className="plan-details-hero__eyebrow">{t('workouts.plan.details.eyebrow')}</div>
							<h1>{plan.name}</h1>
							<Text className="plan-details-hero__subtitle">
								{plan.customerName ? `${plan.customerName} · ` : ''}
								{getDateFormat(plan.startDate, DATE_FORMAT.DD_MM_YYYY)} to {getDateFormat(plan.endDate, DATE_FORMAT.DD_MM_YYYY)}
							</Text>
						</div>
						<div className="plan-details-hero__progress-block">
							<div className="plan-details-hero__status-row">
								<span className={`plan-details-status-pill plan-details-status-pill--${normalizedStatus}`}>
									{STATUS_LABELS[normalizedStatus] || plan.status}
								</span>
								<Text>{currentDayLabel}</Text>
							</div>
							<Progress percent={completionPercent} strokeColor={STATUS_COLORS[normalizedStatus] || STATUS_COLORS.active} showInfo={false} />
							<div className="plan-details-hero__progress-meta">
								<span>{completionPercent}% complete</span>
								<span>{plan.durationDays} total days</span>
							</div>
						</div>
					</div>
					<div className="plan-details-hero__actions">
						<Space wrap>
							{normalizedStatus === 'active' && (
								<Button icon={<PauseCircleOutlined />} onClick={() => dispatch(putApi({}, 'PAUSE_WORKOUT_PLAN', `${SERVER_IP}api/workoutPlan/${id}/pause`))}>
									Pause
								</Button>
							)}
							{normalizedStatus === 'paused' && (
								<Button type="primary" icon={<PlayCircleOutlined />} onClick={() => dispatch(putApi({}, 'RESUME_WORKOUT_PLAN', `${SERVER_IP}api/workoutPlan/${id}/resume`))}>
									Resume
								</Button>
							)}
							<Button icon={<DownloadOutlined />} loading={downloading} disabled={downloading} onClick={handleDownloadPdf}>
								{t('workouts.template.pdf.download_pdf')}
							</Button>
							{(normalizedStatus === 'active' || normalizedStatus === 'paused') && (
								<Popconfirm
									title="Cancel this plan?"
									onConfirm={() => dispatch(putApi({}, 'CANCEL_WORKOUT_PLAN', `${SERVER_IP}api/workoutPlan/${id}/cancel`))}
									okText="Yes, cancel"
									cancelText="No"
								>
									<Button danger icon={<StopOutlined />}>
										Cancel
									</Button>
								</Popconfirm>
							)}
						</Space>
					</div>
				</section>

				<section className="plan-details-summary-grid">
					<Card bordered={false} className="plan-details-summary-card">
						<div className="plan-details-summary-card__icon">
							<CalendarOutlined />
						</div>
						<div>
							<span>Schedule</span>
							<strong>{getDateFormat(plan.startDate, DATE_FORMAT.DD_MM_YYYY)} to {getDateFormat(plan.endDate, DATE_FORMAT.DD_MM_YYYY)}</strong>
						</div>
					</Card>
					<Card bordered={false} className="plan-details-summary-card">
						<div className="plan-details-summary-card__icon">
							<ThunderboltOutlined />
						</div>
						<div>
							<span>Total workouts</span>
							<strong>{totalExercises}</strong>
						</div>
					</Card>
					<Card bordered={false} className="plan-details-summary-card">
						<div className="plan-details-summary-card__icon">
							<CheckCircleOutlined />
						</div>
						<div>
							<span>Training days</span>
							<strong>
								{activeDayCount} active / {restDaysCount} rest
							</strong>
						</div>
					</Card>
				</section>

				<Row gutter={[18, 18]}>
					<Col xs={24} lg={8} xl={7}>
						<Card bordered={false} className="plan-details-days-card" title="Plan timeline">
							<div className="plan-details-days-grid">
								{plan.days?.map((day) => (
									<button
										type="button"
										key={day.dayNumber}
										onClick={() => setSelectedDayNumber(day.dayNumber)}
										className={`plan-details-day-chip ${getDayStateClassName(day)} ${selectedDayNumber === day.dayNumber ? 'is-selected' : ''}`}
									>
										<span className="plan-details-day-chip__number">{day.dayNumber}</span>
										<span className="plan-details-day-chip__label">{day.isRestDay ? 'Rest' : day.title || `${day.items?.length || 0} items`}</span>
									</button>
								))}
							</div>
						</Card>
					</Col>
					<Col xs={24} lg={16} xl={17}>
						{selectedDay ? (
							<Card
								bordered={false}
								className="plan-details-day-card"
								title={
									<div className="plan-details-day-card__title">
										<div>
											<strong>Day {selectedDay.dayNumber}</strong>
											{selectedDayTitle && <Text>{selectedDayTitle}</Text>}
										</div>
									</div>
								}
								extra={selectedDay.isRestDay ? <Badge status="default" text="Rest Day" /> : <Badge status="processing" text={`${selectedDayExerciseCount} exercises`} />}
							>
								{!selectedDay.isRestDay && (
									<div className="plan-details-day-overview">
										<div className="plan-details-day-overview__item">
											<span>Exercises</span>
											<strong>{selectedDayExerciseCount}</strong>
										</div>
										<div className="plan-details-day-overview__item">
											<span>Total rest</span>
											<strong>{selectedDayRestCount}s</strong>
										</div>
										<div className="plan-details-day-overview__item">
											<span>Focus</span>
											<strong>{selectedDayTitle || 'Workout block'}</strong>
										</div>
									</div>
								)}
								{selectedDay?.notes && !selectedDay.isRestDay && <div className="plan-details-day-card__notes">{selectedDay.notes}</div>}
								{selectedDay.isRestDay ? (
									<div className="plan-details-day-card__empty">Recovery day. No exercises scheduled.</div>
								) : (
									<div className="plan-details-exercise-list">
										{selectedDay.items?.map((item, index) => (
											<div key={item.id || index} className="plan-details-exercise-item">
												<div className="plan-details-exercise-item__index">{String(index + 1).padStart(2, '0')}</div>
												<div className="plan-details-exercise-item__content">
													<div className="plan-details-exercise-item__header">
														<div className="plan-details-exercise-item__title-block">
															<strong>{getWorkoutDisplayName(item)}</strong>
															<span className="plan-details-exercise-item__machine">{formatDetailValue(item?.workout?.equipment, 'Machine not added')}</span>
														</div>
														<span>
															{item.sets} sets x {item.reps} reps{getWorkoutRestSeconds(item) ? ` · ${getWorkoutRestSeconds(item)}s rest` : ''}
														</span>
													</div>
													<div className="plan-details-exercise-item__meta">
														{item?.workout?.muscleGroup && <span>{item.workout.muscleGroup}</span>}
														{item?.workout?.equipment && <span>{item.workout.equipment}</span>}
														{item?.workout?.level && <span>{item.workout.level}</span>}
														{item?.workout?.gender && <span>{item.workout.gender}</span>}
													</div>
													<div className="plan-details-exercise-item__detail-grid">
														{getExerciseDetailItems(item).map((detail) => (
															<div key={detail.label} className="plan-details-exercise-item__detail-card">
																<div className="plan-details-exercise-item__detail-label">{detail.label}</div>
																<div className="plan-details-exercise-item__detail-value">{detail.value}</div>
															</div>
														))}
													</div>
													{item?.workout?.instructions && (
														<div className="plan-details-exercise-item__instruction-block">
															<div className="plan-details-exercise-item__section-label">Instructions</div>
															<div className="plan-details-exercise-item__instruction-copy">{item.workout.instructions}</div>
														</div>
													)}
													<div className="plan-details-exercise-item__footer">
														<div className="plan-details-exercise-item__section-label">Workout references</div>
														<div className="plan-details-exercise-item__links">
															{item?.workout?.videoUrl ? (
																<a href={item.workout.videoUrl} target="_blank" rel="noreferrer">
																	Open video guide
																</a>
															) : (
																<span>No video linked</span>
															)}
															{item?.workout?.imageUrl ? (
																<a href={item.workout.imageUrl} target="_blank" rel="noreferrer">
																	Open image reference
																</a>
															) : (
																<span>No image linked</span>
															)}
														</div>
													</div>
													{item.notes && <div className="plan-details-exercise-item__notes">{item.notes}</div>}
												</div>
											</div>
										))}
										{(!selectedDay.items || selectedDay.items.length === 0) && (
											<div className="plan-details-day-card__empty">No exercises added for this day yet.</div>
										)}
									</div>
								)}
							</Card>
						) : (
							<Card bordered={false} className="plan-details-day-card">
								<div className="plan-details-day-card__empty">Select a day from the timeline to review that workout block.</div>
							</Card>
						)}
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default PlanDetails;
